import type { CareerTierId } from '@crowdfpl/domain/gamification';
import { Button } from '@crowdfpl/ui-web/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { type ReactElement } from 'react';
import { CAREER_TIER_ICONS } from '@/components/gamification/career-tier-icons';
import { formatCareerTierLabel } from '@/components/gamification/career-tier-labels';
import {
  clampSegmentProgress,
  identityAccentClass,
  identityIconSurfaceClass,
  identityStrokeClass,
  isMaxTierProgress,
} from '@/components/gamification/progress-identity';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { useAuth } from '@/hooks/auth';
import { useGamificationFeatureEnabled } from '@/hooks/data/config';
import { useMyGamification } from '@/hooks/data/gamification';
import { useProgressTrophiesModalStore } from '@/stores/progress-trophies-modal';

const RING_SIZE = 28;
const RING_STROKE = 2;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function ProgressRing({
  progress,
  tier,
}: {
  progress: number;
  tier: CareerTierId | null;
}): ReactElement {
  const safeProgress = clampSegmentProgress(progress);
  const filledLength = safeProgress * RING_CIRCUMFERENCE;
  const strokeClass = tier ? identityStrokeClass(tier) : 'stroke-primary';

  return (
    <svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      className="absolute inset-0 -rotate-90"
      aria-hidden
    >
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        className="fill-none stroke-muted"
        strokeWidth={RING_STROKE}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        className={cn(
          'fill-none transition-[stroke-dasharray] duration-300 motion-reduce:transition-none',
          strokeClass,
        )}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${filledLength} ${RING_CIRCUMFERENCE}`}
      />
    </svg>
  );
}

type TierBadgeButtonProps = {
  className?: string;
};

export function TierBadgeButton({ className }: TierBadgeButtonProps): ReactElement | null {
  const gamificationEnabled = useGamificationFeatureEnabled();
  const isAuthenticated = useAuth(state => state.isAuthenticated);
  const openOwnProgress = useProgressTrophiesModalStore(state => state.openOwnProgress);
  const { data, isLoading } = useMyGamification();

  if (!gamificationEnabled || !isAuthenticated) {
    return null;
  }

  const progress = data?.progress;
  const tier = progress?.tier ?? null;
  const tierLabel = tier ? formatCareerTierLabel(tier) : 'Progress';
  const segmentProgress = clampSegmentProgress(progress?.segment_progress ?? 0);
  const progressPercent = Math.round(segmentProgress * 100);
  const hasUnseen = data?.updates.has_unseen ?? false;
  const hasData = Boolean(data);
  const maxTier = progress ? isMaxTierProgress(progress) : false;
  const nextTierLabel =
    progress?.next_tier != null ? formatCareerTierLabel(progress.next_tier) : null;
  const Icon = tier ? CAREER_TIER_ICONS[tier] : CAREER_TIER_ICONS.rookie;

  const progressHint = maxTier
    ? 'Max tier'
    : nextTierLabel
      ? `${progressPercent}% to ${nextTierLabel}`
      : isLoading && !hasData
        ? 'Loading progress'
        : `${progressPercent}% to next`;

  const ariaLabel = [
    'Progress & trophies',
    hasData ? `${tierLabel} tier` : null,
    hasData ? progressHint : null,
    hasUnseen ? 'New rewards available' : null,
    'New feature',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              trackUmamiEvent('gamification_modal_open', {
                source: 'header_badge',
                tab: 'progress',
                read_mode: 'own',
              });
              openOwnProgress();
            }}
            aria-label={ariaLabel}
            className={cn('relative h-11 w-11 min-h-11 min-w-11', className)}
          >
            <span
              aria-hidden
              data-testid="tier-badge-new-plaque"
              className="pointer-events-none absolute left-0 top-1.5 z-10 rounded-sm bg-gold px-0.5 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-gold-foreground ring-1 ring-background dark:text-background"
            >
              New
            </span>

            <span className="relative inline-flex h-7 w-7 items-center justify-center">
              <ProgressRing progress={segmentProgress} tier={tier} />
              <span
                className={cn(
                  'flex h-4.5 w-4.5 items-center justify-center rounded-full border',
                  tier ? identityIconSurfaceClass(tier) : 'border-primary bg-primary/10 text-primary',
                )}
              >
                <Icon className="h-3 w-3" strokeWidth={2.6} aria-hidden />
              </span>
            </span>

            {hasUnseen ? (
              <span
                data-testid="tier-badge-red-dot"
                className="absolute right-1 top-1.5 flex h-2.5 w-2.5"
                aria-hidden
              >
                <span className="absolute inset-0 rounded-full bg-destructive/60 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background" />
              </span>
            ) : null}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className={cn('font-semibold', tier ? identityAccentClass(tier) : undefined)}>
            {tierLabel}
          </div>
          <div className="text-muted-foreground">{progressHint}</div>
          {hasUnseen ? <div className="mt-0.5 font-medium text-destructive">New unlock</div> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
